import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOpenPosDineInMutation } from "../../store/api";
import { useStoreFeatures } from "../../hooks/useStoreFeatures";
import { isChannelOrderingBlocked } from "../../utils/orderBlocking";
import {
  getPosErrorMessage,
  isOrderingBlockedError,
  ORDERING_BLOCKED_MESSAGE,
} from "../../utils/posErrorHandler";
import { useSubmitCashSessionGuard } from "../../hooks/useSubmitCashSessionGuard";
import { usePosSessionPrompt } from "../../context/PosSessionPromptContext";

const DineInEntry = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const isOpeningRef = useRef(false);
  const [isOpening, setIsOpening] = useState(false);
  const openAttemptedRef = useRef(false);
  const sessionPromptRequestedRef = useRef(false);
  const sessionPrompt = usePosSessionPrompt();

  const [openDineIn] = useOpenPosDineInMutation();
  const {
    features,
    isLoading: isStoreFeaturesLoading,
    refetch: refetchStoreFeatures,
  } = useStoreFeatures(true);
  const isOrderBlocked = isChannelOrderingBlocked(features, "POS");
  const cashSessionGuard = useSubmitCashSessionGuard();
  useEffect(() => {
    openAttemptedRef.current = false;
    setError(null);
  }, []);

  const openDineInOrder = useCallback(
    async (payload) => {
      if (isOpeningRef.current) return;
      isOpeningRef.current = true;
      setIsOpening(true);
      setError(null);

      if (isStoreFeaturesLoading && !features) {
        setIsOpening(false);
        isOpeningRef.current = false;
        return;
      }

      if (isOrderBlocked) {
        setError(ORDERING_BLOCKED_MESSAGE);
        setIsOpening(false);
        isOpeningRef.current = false;
        return;
      }

      try {
        const result = await openDineIn(payload).unwrap();
        if (result?.orderId) {
          navigate(`/pos/dinein/${result.orderId}`, { replace: true });
        }
      } catch (err) {
        if (isOrderingBlockedError(err)) {
          refetchStoreFeatures();
        }
        setError(getPosErrorMessage(err));
      } finally {
        setIsOpening(false);
        isOpeningRef.current = false;
      }
    },
    [
      features,
      isOrderBlocked,
      isStoreFeaturesLoading,
      openDineIn,
      navigate,
      refetchStoreFeatures,
    ],
  );

  useEffect(() => {
    if (isStoreFeaturesLoading && !features) return;
    if (openAttemptedRef.current) return;
    if (cashSessionGuard.isCheckingCashSession) return;

    if (!cashSessionGuard.hasActiveCashSession) {
      if (sessionPromptRequestedRef.current) return;
      sessionPromptRequestedRef.current = true;
      sessionPrompt.setSessionPromptMessage(
        "Please start a session before placing an order.",
      );
      sessionPrompt.requestStartSession();
      return;
    }

    openAttemptedRef.current = true;
    openDineInOrder({
      channel: "POS",
      sourceSystem: "EDGE",
    });
  }, [
    cashSessionGuard.hasActiveCashSession,
    cashSessionGuard.isCheckingCashSession,
    openDineInOrder,
    sessionPrompt,
  ]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-950 px-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-3 text-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-600 dark:border-slate-700 dark:border-t-indigo-400" />
        {isOpening && (
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Opening dine-in...
          </div>
        )}

        {error && (
          <div className="text-xs text-rose-600 dark:text-rose-400">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default DineInEntry;
